/**
 * Step — Look. Controls for the generated welcome design: one adjustment box
 * and one Regenerate button. The server routes the hint to the orientation(s)
 * the user actually mentioned ("the laptop font…" → landscape only; a general
 * note → both) and edits the existing design instead of restyling from
 * scratch.
 *
 * The booth itself is rendered by the shell's persistent PreviewPane
 * (Onboarding.tsx), where the Phone/Laptop toggle also lives — so a hint aimed
 * at one orientation can be checked without leaving the step.
 */

import React, { useState } from 'react'
import { FaRedo, FaSpinner, FaImage } from 'react-icons/fa'
import { useOnboardingStore } from '../onboardingStore'
import type { RegenTarget } from '../onboardingApi'
import { useCopy } from '../regionCopy'

interface StepBackgroundsProps {
  onRegen: (target: RegenTarget, hint?: string) => void
  onRefine: () => void
}

export const StepBackgrounds: React.FC<StepBackgroundsProps> = ({ onRegen, onRefine }) => {
  const spec = useOnboardingStore((s) => s.spec)
  const welcomeDesign = useOnboardingStore((s) => s.welcomeDesign)
  const assets = useOnboardingStore((s) => s.assets)
  const remaining = useOnboardingStore((s) => s.remaining)
  const regenInFlight = useOnboardingStore((s) => s.regenInFlight)
  const [hint, setHint] = useState('')
  const c = useCopy()

  const regensLeft = remaining?.regens ?? 0
  const fullGensLeft = remaining?.fullGenerations ?? 0
  const welcomeBusy =
    regenInFlight === 'welcome' ||
    regenInFlight === 'welcomeBgPortrait' ||
    regenInFlight === 'welcomeBgLandscape'

  if (!spec) return null

  // A generation can come back with no welcome design at all (every design
  // attempt failed server-side). Blanking the first step made that read as
  // a broken page with nothing to press — say what happened and offer the
  // two ways forward instead.
  if (!welcomeDesign) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 px-6 py-6 text-center">
        <FaImage className="text-3xl text-whiteout/25" />
        <p className="max-w-xs text-sm text-whiteout/60">{c.welcomeDesignMissing}</p>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => onRegen('welcome')}
            disabled={regenInFlight !== null || regensLeft <= 0}
            className="inline-flex items-center gap-2 rounded-full bg-blueSparkle px-5 py-2 text-sm font-semibold text-whiteout transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {welcomeBusy ? <FaSpinner className="animate-spin" /> : <FaRedo />}
            {c.redrawWelcome}
          </button>
          <button
            type="button"
            onClick={onRefine}
            className="text-sm text-whiteout/60 underline-offset-2 hover:underline"
          >
            {c.editPrompt}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col items-center overflow-y-auto soft-scrollbar px-6 py-6">
      <div className="flex w-full max-w-md flex-col gap-5">
        <header>
          <h2 className="text-xl font-semibold">{c.welcomeDesign}</h2>
          <p className="text-sm text-whiteout/50">{c.welcomeDesignSub}</p>
        </header>

        <label className="block space-y-2">
          <span className="text-xs font-medium uppercase tracking-wide text-whiteout/50">
            {c.adjustmentLabel}
          </span>
          <input
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            placeholder={c.adjustmentPlaceholder}
            maxLength={300}
            className="w-full rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm text-whiteout placeholder-whiteout/30 outline-none focus:border-blueSparkle"
          />
        </label>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={regensLeft <= 0 || regenInFlight !== null}
            onClick={() => onRegen('welcome', hint.trim() || undefined)}
            className="inline-flex items-center gap-2 rounded-full bg-blueSparkle px-5 py-2.5 text-sm font-semibold text-whiteout shadow-lg transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {welcomeBusy ? <FaSpinner className="animate-spin" /> : <FaRedo />}
            {c.redrawWelcome}
          </button>
        </div>

        {/* App background */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <div className="flex items-center gap-4">
            {assets?.appBg ? (
              <img
                src={assets.appBg}
                alt="App background"
                className="h-16 w-16 rounded-xl object-cover ring-1 ring-white/15"
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-white/[0.05] text-whiteout/30 ring-1 ring-white/10">
                <FaImage />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">{c.appBackground}</div>
              <div className="text-xs text-whiteout/50">{c.appBackgroundSub}</div>
            </div>
            <button
              type="button"
              disabled={regensLeft <= 0 || regenInFlight !== null}
              onClick={() => onRegen('appBg', hint.trim() || undefined)}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs text-whiteout/70 transition-colors hover:border-blueSparkle/60 hover:text-whiteout disabled:cursor-not-allowed disabled:opacity-40"
            >
              {regenInFlight === 'appBg' ? <FaSpinner className="animate-spin" /> : <FaRedo />}
              {c.reroll}
            </button>
          </div>
        </div>

        <p className="text-[11px] text-whiteout/35">
          {c.regensLeft(regensLeft)} ·{' '}
          <button
            type="button"
            onClick={onRefine}
            disabled={fullGensLeft <= 0}
            className="underline decoration-dotted hover:text-whiteout/70 disabled:no-underline disabled:opacity-50"
          >
            {c.redoEverything(fullGensLeft)}
          </button>
        </p>
      </div>
    </div>
  )
}

export default StepBackgrounds
